import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';
import path from 'path';
import slash from 'slash';
import {
  e57FileFormat,
  encompassFileFormat,
  potreeFileFormat,
  lasFileFormat
} from '../file-extensions';
import { defaultTagTexture } from '../viewer/textures/tag-icons';

export interface CameraData {
  id: string;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  path: string;
  name: string;
  config?: object
}

export interface OrthoData {
  imageBounds: [];
  imageDataSplit: [];
  imageData: string;
  imageSize: [number, number];
  imageSizeOriginal: [number, number];
  imagePath: string;
  scaleX: number,
  scaleY: number,
}

export interface TagData {
  id: string;
  name: string;
  comment: string;
  x: number;
  y: number;
  z?: number;
}

export interface LayerData {
  id: string;
  name: string;
  visible: boolean;
  color: string;
}

export interface Asset {
  id: string;
  name: string;
  path: string;
  fromJSON: boolean;
  date: string;
  visible: boolean;
  folderID: string;
  type: AssetType;
  texture?: string;
  data: OrthoData | CameraData[] | TagData[] | LayerData[];
}

export interface AssetListItem {
  folderID: string;
  assetPath: string;
  type: AssetType;
  data?: OrthoData | CameraData[] | TagData[] | LayerData[];
}

// NOTE: Always add new asset types to bottom of list
export enum AssetType {
  LAS,
  Encompass,
  Panoramic,
  Unknown,
  OrthoMosaic,
  Planar,
  E57Points,
  Tag,
  IFC,
  Potree,
  SHP,
  DXF
}

export const pointCloudType = (ext: string): AssetType => {
  if (ext === lasFileFormat) {
    return AssetType.LAS;
  } else if (ext === encompassFileFormat) {
    return AssetType.Encompass;
  } else if (ext === e57FileFormat) {
    return AssetType.E57Points;
  } else if (ext === potreeFileFormat) {
    return AssetType.Potree;
  } else {
    return AssetType.Unknown;
  }
};

export const isCameraOrTag = (assetType) => {
  return (assetType === AssetType.Tag)
    || (assetType === AssetType.Planar)
    || (assetType === AssetType.Panoramic);
};

const getAssetPayload = (folderID: string, assetPath: string, assetType: AssetType, assetData?: CameraData[] | OrthoData | TagData[] | LayerData[]): Asset => {
  assetPath = slash(assetPath);

  const texture = (assetType === AssetType.Tag) ? defaultTagTexture : undefined;

  // Add unique ID to cameras and tags
  if (isCameraOrTag(assetType)) {
    assetData = (assetData as any).map(data => {
      return {
        id: nanoid(),
        ...data
      };
    });
  }

  return {
    id: nanoid(),
    name: path.basename(assetPath),
    path: assetPath,
    date: new Date().toISOString(),
    type: assetType,
    folderID,
    visible: true,
    texture,
    fromJSON: false,
    data: assetData
  } as Asset;
};

export const assetsSlice = createSlice({
  name: 'assets',
  initialState: [] as Asset[],
  reducers: {
    addAsset: {
      reducer: (state, action: PayloadAction<Asset>) => {
        state.push(action.payload);
      },
      prepare: (folderID: string, assetPath: string, type: AssetType, data: CameraData[] | OrthoData | TagData[] | LayerData[]): {payload: Asset} =>
        ({payload: getAssetPayload(folderID, assetPath, type, data)})
    },
    addAssets: {
      /** Add multiple assets at once. Much faster than adding single assets */
      reducer: (state, action: PayloadAction<Asset[]>) => {
        action.payload.forEach(payload => {
          state.push(payload);
        });
      },
      prepare: (assetsList: AssetListItem[]): {payload: Asset[]} => {
        const payload = assetsList.map(assetItem => {
          const {folderID, assetPath, type, data} = assetItem;
          return getAssetPayload(folderID, assetPath, type, data);
        });

        return {payload};
      }
    },
    addNewTag: (state, action) => {
      const {assetID, tagData} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      // Add unique ID to new tag
      const newTagData = {
        id: nanoid(),
        ...tagData
      };

      asset.data = [
        ...(asset.data as any),
        newTagData
      ];
    },
    changeTagContent: (state, action: PayloadAction<{tagID: string, name: string, comment: string}>) => {
      const {tagID, comment, name} = action.payload;

      state.forEach(asset => {
        if (asset.type !== AssetType.Tag) {
          return;
        }

        const tag = (asset.data as any).find(tag => tag.id === tagID);
        if (!tag) return;

        tag.name = name;
        tag.comment = comment;
      });
    },
    changeTexture: (state, action: PayloadAction<{texture: string, assetID: string}>) => {
      const {assetID, texture} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.texture = texture;
    },
    changeFolder: (state, action: PayloadAction<{folderID: string, assetID: string}>) => {
      const {assetID, folderID} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.folderID = folderID;
    },
    changeName: (state, action) => {
      const {assetID, name} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.name = name;
    },
    toggleVisibility: (state, action) => {
      const assetID = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.visible = !asset.visible;
    },
    toggleFolderVisibility: (state, action) => {
      const {folderID, visible} = action.payload;
      const assets = state.filter(asset => asset.folderID === folderID);

      assets.forEach(asset => {
        asset.visible = visible;
      });
    },
    setVisibility: (state, action) => {
      const {assetID, visible} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.visible = visible;
    },
    setVisibleAssets: (state, action) => {
      const visibleAssets = action.payload;
      state.forEach(asset => {
        asset.visible = visibleAssets.includes(asset.id);
      });
    },
    setVisibleFolder: (state, action) => {
      const folderID = action.payload;
      state.forEach(asset => {
        asset.visible = asset.folderID === folderID;
      });
    },
    deleteAssetData: (state, action) => {
      state.forEach(asset => {
        if (!isCameraOrTag(asset.type)) {
          return;
        }

        asset.data = (asset.data as any).filter(row  => {
          return !action.payload.includes(row.id);
        });
      });
    },
    deleteAsset: (state, action) =>
      state.filter(asset => asset.id !== action.payload),
    deleteAssets: (state, action) =>
      state.filter(asset => asset.folderID !== action.payload)
  }
});

export default assetsSlice.reducer;

export const {
  addAsset,
  addAssets,
  addNewTag,
  changeTagContent,
  changeFolder,
  changeTexture,
  setVisibleAssets,
  toggleVisibility,
  toggleFolderVisibility,
  setVisibility,
  setVisibleFolder,
  changeName,
  deleteAssetData,
  deleteAsset,
  deleteAssets,
} = assetsSlice.actions;

export const selectAllAssets = state =>
  state.assets;

export const getModelAssets = assets => assets.filter(asset => {
  return (asset.type === AssetType.IFC)
    || (asset.type === AssetType.SHP)
    || (asset.type === AssetType.DXF);
});

export const getTagAssets = assets =>
  assets.filter(asset => asset.type === AssetType.Tag);

export const getPointCloudAssets = assets => assets.filter(asset => {
  return (asset.type === AssetType.Encompass)
    || (asset.type === AssetType.LAS)
    || (asset.type === AssetType.E57Points)
    || (asset.type === AssetType.Potree);
});

export const getCameraFileAssets = assets =>
  assets.filter(asset => asset.type === AssetType.Panoramic || asset.type === AssetType.Planar);

export const getCameraListFromAssets = (assets, checkVisible) => {
  const cameraAssets = getCameraFileAssets(assets);

  return cameraAssets.map(cameraFile => {
    if (checkVisible && !cameraFile.visible) return [];

    return cameraFile.data.map(camera => {
      return {
        ...camera,
        id: `${cameraFile.id}-${camera.name}`,
        csv: cameraFile.id,
        folder: cameraFile.folderID,
        assetType: cameraFile.type,
        fromJSON: cameraFile.fromJSON,
      };
    });
  }).flat();
};

export const getOrthoAssets = assets =>
  assets.filter(asset => asset.type === AssetType.OrthoMosaic);

export const selectAssetByID = assetID => state =>
  state.assets.find(asset => asset.id === assetID);

export const selectAssetByFolderID = folderID => state =>
  state.assets.filter(asset => asset.folderID === folderID);